import React, { useEffect, useState } from "react";
import { ArrowBack } from "@mui/icons-material";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { tokens } from "../../../theme";
import { authorizedPost, checkPropertyOfferRates, toggleLoading } from "../../../store/actions";
import { LIST_PROPERTY_OFFER_DETAILS } from "../../../graphql";
import AddNewRate from "./addNewRate";
import RatesPreviewModal from "./RatesPreviewModal";
import moment from "moment";

function RateDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [data, setData] = useState({});
    const [isUpdating, setIsUpdating] = useState(false);
    const [rates, setRates] = useState([]);
    const [ratesModal, setRatesModal] = useState(false);
    const [selectedOfferContext, setSelectedOfferContext] = useState(null);

    useEffect(() => {
        document.title = 'Offer Details';
        fetchData();
        // eslint-disable-next-line
    }, [dispatch, id, isUpdating]);

    const fetchData = async () => {
        dispatch(toggleLoading(true));
        let res = await authorizedPost(LIST_PROPERTY_OFFER_DETAILS, { id: id });
        if (res.status) {
            setData(res.data.list_property_offer_details.data);
        }
        dispatch(toggleLoading(false));
    }

    const handleCheckRatesButton = async (val, idx) => {
        if (!val.plus_percentage && !val.minus_percentage) {
            return;
        }
        let req = {
            startDate: data.start_date,
            endDate: data.end_date,
            plusPercentage: val.plus_percentage || 0,
            minusPercentage: val.minus_percentage || 0,
        };
        setSelectedOfferContext({
            startDate: data.start_date,
            endDate: data.end_date,
            percentage: val.percentage,
            prevPercentage: idx === 0 ? 100 : data?.percentages[idx - 1]?.percentage,
            plusPercentage: val.plus_percentage || 0,
            minusPercentage: val.minus_percentage || 0,
            plusOrMinus: val.plus_percentage > 0 ? "plus" : "minus",
        });
        let ratesRes = await dispatch(checkPropertyOfferRates(req));
        if (ratesRes) {
            setRates(ratesRes);
            setRatesModal(true);
        }
    }

    const KeyAndValue = ({ keyName, value, render, sx, colored, fullWidth }) => (
        <Grid item md={fullWidth ? 12 : 6} xs={12}>
            <Stack direction="row" spacing={2} sx={sx}>
                <Typography width={"50%"}>{keyName}:</Typography>
                {value ?
                    <Typography color={colored ? "primary" : ""}>
                        {value}
                    </Typography>
                    : render}
            </Stack>
        </Grid>
    )

    return (
        <Box m="20px" mb={15}>
            {!isUpdating ? (
                <>
                    <Stack
                        direction={"row"}
                        justifyContent={"space-between"}
                        alignItems={"flex-end"}
                        spacing={2}
                        m="20px 0"
                    >
                        <Button size="small" variant="outlined" onClick={() => navigate(-1)}>
                            <ArrowBack fontSize="small" />
                        </Button>
                        <Typography fontWeight="bold" variant="h4">
                            OFFER DETAILS
                        </Typography>
                        <Button
                            size='small'
                            variant='outlined'
                            onClick={() => setIsUpdating(isUpdating ? false : true)}
                        >
                            <b>Update</b>
                        </Button>
                    </Stack>
                    <Box
                        width="100%"
                        p={2}
                        borderRadius={1}
                        backgroundColor={colors.primary[400]}
                    >
                        <Grid container rowSpacing={2} columnSpacing={4}>
                            <KeyAndValue keyName={"Type"} value={data?.type?.replace("_", " ") || "-"} sx={{ textTransform: "capitalize" }} colored />
                            <KeyAndValue keyName={"Start date"} value={data?.start_date ? moment.unix(data?.start_date).format("DD-MM-YYYY") : "-"} />
                            <KeyAndValue keyName={"End date"} value={data?.end_date ? moment.unix(data?.end_date).format("DD-MM-YYYY") : "-"} />
                            <KeyAndValue keyName={"Last sync"} value={data?.last_sync ? moment.unix(data?.last_sync).format("DD-MMM-YYYY - HH:mm") : "-"} />
                            <KeyAndValue keyName={"Sync status"} value={data?.sync_status || "-"} />
                        </Grid>
                    </Box>
                    <Grid container spacing={2} mt={1}>
                        {data.type === "occupancy_based" && data?.percentages?.map((el, idx) => (
                            <Grid item xs={6} key={idx}>
                                <Box
                                    width="100%"
                                    p={2}
                                    borderRadius={1}
                                    backgroundColor={colors.primary[400]}
                                >
                                    <KeyAndValue keyName={"Percentage"} value={`${el.percentage || "0"}%`} />
                                    {el.minus_percentage > 0 ? (
                                        <KeyAndValue keyName={"Rate"} value={`${el.minus_percentage}% ↓`} />
                                    ) : null}
                                    {el.plus_percentage > 0 ? (
                                        <KeyAndValue keyName={"Rate"} value={`${el.plus_percentage}% ↑`} />
                                    ) : null}
                                    <Typography variant="body2" color={colors.grey[300]} sx={{ fontStyle: "italic", my: 0.5 }}>
                                        {`If ${el.percentage}% - ${idx === 0 ? "100" : data?.percentages[idx - 1].percentage}% room is available, rate will ${el.plus_percentage > 0 ? "increase" : "decrease"}`}
                                    </Typography>
                                    <Button
                                        size='small'
                                        variant='contained'
                                        onClick={() => handleCheckRatesButton(el, idx)}
                                        sx={{mt: 1}}
                                        disabled={!el.plus_percentage && !el.minus_percentage}
                                    >
                                        Check rates
                                    </Button>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </>
            ) : (
                <AddNewRate colors={colors} dispatch={dispatch} setIsAdding={setIsUpdating} updatingData={data} />
            )}

            <RatesPreviewModal
                open={ratesModal}
                onClose={() => setRatesModal(false)}
                ratesData={rates}
                offerContext={selectedOfferContext}
            />
        </Box>
    );
}

export default RateDetails;
